import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { CoreModule } from '@core/core.module';
import {
	EmailRecipientType,
	GlobalEmailTemplateMetadata,
} from '@models/global-email-templates';
import { MatterFileType } from '@models/matter';
import { dispatch, select } from '@ngxs/store';
import { NavigationService } from '@services/nav/nav.service';
import {
	CreateBusinessEmailTemplate,
	DeleteBusinessEmailTemplate,
} from '@state/email-templates/business-email-templates/business-email-templates.actions';
import { CreateGlobalEmailTemplate } from '@state/email-templates/global-email-templates/global-email-templates.actions';
import { ButtonComponent } from '@ui/button/button.component';
import { DropdownItem } from '@ui/dropdown/dropdown.component';
import { IconsModule } from '@ui/icons/icons.module';
import { UI_TYPES } from 'src/app/ui/modal/modal.types';
import { SelectComponent } from '@ui/select/select.component';
import { DropdownComponent } from 'src/app/ui/dropdown/dropdown.component';
import { ContentHeaderComponent } from '../../../../ui/content-header/content-header.component';
import {
	emailRecipientTypeOptions,
	fileTypeOptions,
} from './email-templates.consts';
import { $emailTemplatesViewModel } from './email-templates.selectors';

const DEFAULT_TEMPLATE: Omit<GlobalEmailTemplateMetadata, 'id'> = {
	templateName: 'New Template',
	emailRecipientType: EmailRecipientType.ALL,
	emailRecipientSubType: null,
	fileType: null,
	default: false,
	createdBy: null,
	createdAt: null,
	updatedAt: null,
	versionId: null,
	source: null,
};

@Component({
	selector: 'app-email-templates',
	templateUrl: './email-templates.component.html',
	standalone: true,
	imports: [
		CommonModule,
		CoreModule,
		SelectComponent,
		ButtonComponent,
		ContentHeaderComponent,
		IconsModule,
		DropdownComponent,
	],
})
export class EmailTemplatesComponent {
	navService = inject(NavigationService);
	viewModel_ = select($emailTemplatesViewModel);
	createBusinessEmailTemplate = dispatch(CreateBusinessEmailTemplate);
	createGlobalEmailTemplate = dispatch(CreateGlobalEmailTemplate);
	deleteBusinessEmailTemplate = dispatch(DeleteBusinessEmailTemplate);

	templates_ = computed(() => Object.values(this.viewModel_().emailTemplates));

	templatesWithDropdownItems_ = computed(() =>
		this.templates_().map((template) => ({
			...template,
			recipient: emailRecipientTypeOptions.find(
				(option) => option.id === template.emailRecipientType,
			)?.text,
			dropdownItems: [
				{
					id: 'delete',
					title: {
						label: 'Delete',
						icon: 'trash',
					},
					type: UI_TYPES.DANGER,
					action: () => this.onDeleteTemplate(template.id),
				},
			] as DropdownItem[],
		})),
	);

	MATTER_FILE_TYPES = MatterFileType;
	EMAIL_RECIPIENT_TYPE_OPTIONS = emailRecipientTypeOptions;
	FILE_TYPE_OPTIONS = fileTypeOptions;

	clientOptions_ = computed(() => {
		const usersFromTemplates = this.templates_().map(
			(template) => template.createdByUser,
		);
		const uniqueUsers = [...new Set(usersFromTemplates)].map((user) => ({
			text: user?.firstName + ' ' + user?.lastName,
			id: user?.id,
		}));
		return [{ text: 'All', id: 'all' }, ...uniqueUsers];
	});

	onNewTemplate() {
		this.createBusinessEmailTemplate(
			{
				...DEFAULT_TEMPLATE,
				createdBy: this.viewModel_().currentUserId,
				createdAt: Date.now(),
				updatedAt: Date.now(),
			},
			(templateId) => {
				this.navService.goToBusinessManageEmailTemplates_Template(templateId);
			},
		);
	}

	onNewCloserGlobalTemplate() {
		this.createGlobalEmailTemplate(
			{
				...DEFAULT_TEMPLATE,
				source: 'global',
				createdBy: this.viewModel_().currentUserId,
				createdAt: Date.now(),
				updatedAt: Date.now(),
			},
			(templateId) => {
				this.navService.goToBusinessManageEmailTemplates_Template(templateId);
			},
		);
	}

	onSelectTemplate(templateId: string) {
		this.navService.goToBusinessManageEmailTemplates_Template(templateId);
	}

	onDeleteTemplate(templateId: string) {
		this.deleteBusinessEmailTemplate(templateId);
	}
}
